import {
  AlertTriangle,
  Clock,
  FileCheck,
  Package,
  ChevronRight,
} from "lucide-react";
import { Card, Spinner } from "react-bootstrap";
import "./QuickActions.css";
import { api } from "helpers/api_url";
import { Axios } from "api/axios";
import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { CustomColumnDef } from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import {
  fetchQuickActionList,
  QuickActionType,
} from "slices/Dashboard/Quick_Actions/thunk";
import { WarehouseOption } from "./DashboardFilters";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import { DashboardSlidePanel } from "./Reusable/DashboardSlidePanel";

interface QuickActionsProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

export function QuickActions({
  dateFrom,
  dateTo,
  warehouse = [],
}: QuickActionsProps) {
  const dispatch = useDispatch<any>();
  const [quickActionsData, setQuickActionsData] = useState<any>([]);
  const [quickActionsLoading, setQuickActionsLoading] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedActionType, setSelectedActionType] =
    useState<QuickActionType | null>(null);

  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Get data from Redux store
  const { quickActionList, totalRecords, listLoading } = useSelector(
    (state: any) => state.QuickActionsReducer
  );

  const handleCardClick = (actionType: QuickActionType) => {
    setSelectedActionType(actionType);
    setIsPanelOpen(true);
    setPageIndex(0); // Reset to first page when opening panel
  };

  // Build all filters including column filters
  const allFilters = useMemo(() => {
    const filters = [...filterValue];
    return filters;
  }, [filterValue]);

  // Fetch quick action list data
  useEffect(() => {
    if (!isPanelOpen || !selectedActionType) return;

    dispatch(
      fetchQuickActionList({
        page: pageIndex + 1,
        pageSize,
        actionType: selectedActionType,
        dateFrom,
        dateTo,
        warehouse,
        sorting,
        filters: allFilters,
      })
    ).catch((error: any) => {
      console.error("Error fetching quick action list:", error);
      toast.error("Failed to load quick action list");
    });
  }, [
    dispatch,
    isPanelOpen,
    pageIndex,
    pageSize,
    sorting,
    allFilters,
    selectedActionType,
    dateFrom,
    dateTo,
    warehouse,
  ]);

  // Reset page when filters change
  useEffect(() => {
    if (isPanelOpen) {
      setPageIndex(0);
    }
  }, [
    sorting,
    pageSize,
    columnFilters,
    selectedActionType,
    dateFrom,
    dateTo,
    warehouse,
    isPanelOpen,
  ]);

  const getPanelTitle = () => {
    const action = quickActionsData.find(
      (a: any) => a.actionType === selectedActionType
    );
    return action?.title || "Quick Action Details";
  };

  const getStatusBadge = (item: any) => {
    if (item?.status?.toLowerCase() === "low stock") {
      return (
        <span className="inventory-summary-badge inventory-summary-badge-low">
          <AlertTriangle className="inventory-summary-badge-icon" />
          Low Stock
        </span>
      );
    }
    return (
      <span className="inventory-summary-badge inventory-summary-badge-available">
        Available
      </span>
    );
  };
  const buildWarehouseUrl = useCallback(
    (baseUrl: any, warehouseList: any = []) => {
      const params = new URLSearchParams();

      if (dateFrom) {
        params.append("date_from", dateFrom.toISOString().split("T")[0]);
      }
      if (dateTo) {
        params.append("date_to", dateTo.toISOString().split("T")[0]);
      }
      if (warehouseList.length > 0) {
        const warehouseIds =
          warehouseList
            ?.map((w: any) => w.warehouse_id as string)
            ?.filter(Boolean) || [];
        if (warehouseIds.length > 0) {
          params.append("warehouse_id", warehouseIds.join(","));
        }
      }

      const queryString = params.toString();
      return `${baseUrl}${queryString ? `?${queryString}` : ""}`;
    },
    [dateFrom, dateTo]
  );

  useEffect(() => {
    setQuickActionsLoading(true);
    const url = buildWarehouseUrl(
      `${api?.dashboad?.quick_action_card_count}`,
      warehouse
    );
    Axios.get(`${url}`)
      .then((response) => {
        const data = response.data?.data;
        const formattedData = [
          {
            id: 1,
            icon: AlertTriangle,
            iconBgClass: "quick-action-icon-container-red",
            iconColorClass: "quick-action-icon-red",
            title: "Low Stock Alerts",
            count: data?.low_stock_count || 0,
            description: "Items need reordering",
            actionText: "Review Items",
            priority: "high",
            actionType: "low_stock" as QuickActionType,
          },
          {
            id: 3,
            icon: FileCheck,
            iconBgClass: "quick-action-icon-container-blue",
            iconColorClass: "quick-action-icon-blue",
            title: "Quotation Requests",
            count: data?.quotation_request_count || 0,
            description: "Enquiries requests for quotation",
            actionText: "View Now",
            priority: "medium",
            actionType: "quotation_approval" as QuickActionType,
          },
          {
            id: 2,
            icon: Clock,
            iconBgClass: "quick-action-icon-container-orange",
            iconColorClass: "quick-action-icon-orange",
            title: "Pending Orders",
            count: data?.pending_order_count || 0,
            description: "Orders needs to be dispatched",
            actionText: "View Now",
            priority: "medium",
            actionType: "pending_orders" as QuickActionType,
          },

          {
            id: 4,
            icon: Package,
            iconBgClass: "quick-action-icon-container-purple",
            iconColorClass: "quick-action-icon-purple",
            title: "Pending Dispatch",
            count: data?.pending_dispatch_count || 0,
            description: "Orders ready to dispatch",
            actionText: "View Now",
            priority: "medium",
            actionType: "pending_dispatch" as QuickActionType,
          },
        ];
        setQuickActionsData(formattedData);
        setQuickActionsLoading(false);
      })
      .catch((error) => {
        toast.error("Failed to load dashboard metrics");
        console.error("Error fetching dashboard metrics:", error);
        setQuickActionsLoading(false);
      });
  }, [warehouse, buildWarehouseUrl]);

  const columns = useMemo<CustomColumnDef<any>[]>(() => {
    if (!selectedActionType) return [];

    switch (selectedActionType) {
      case "low_stock":
        return [
          {
            header: "Item Name",
            accessorKey: "item_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_low_stock_list_filter_dropdown}/item_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              const item = row.original;
              const stock =
                item.total_available_quantity || item.available_quantity || 0;
              const reorderLevel = item.reorder_level || 0;
              const status =
                stock <= reorderLevel && stock > 0 ? "low" : "available";
              return (
                <div data-status={status}>
                  <span className="quick-actions-table-styles">
                    {item.item_name || "-"}
                  </span>
                </div>
              );
            },
          },
          {
            header: "Item Code",
            accessorKey: "item_code",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_low_stock_list_filter_dropdown}/item_code`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.item_code || "-"}
                </span>
              );
            },
          },
          {
            header: "Category",
            accessorKey: "category_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_low_stock_list_filter_dropdown}/category_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.category_name || "-"}
                </span>
              );
            },
          },
          {
            header: "Warehouse Name",
            accessorKey: "warehouse_name",
            enableSorting: false,
            enableColumnFilter: false,
            filterType: "dropdown",
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original?.warehouse_name || "All Warehouse"}
                </span>
              );
            },
          },
          {
            header: "Available Stock",
            accessorKey: "available_stock",
            enableColumnFilter: false,
            enableSorting: true,
            cell: ({ row }: { row: any }) => {
              const item = row.original;
              const isLowStock = item?.status?.toLowerCase() === "low stock";
              const unit = item.unit_name || item.unit || "";
              return (
                <span
                  className={`quick-actions-table-styles ${
                    isLowStock ? "low-stock" : ""
                  }`}
                >
                  {/* {stock.toLocaleString()} <span className="inventory-summary-stock-unit">{unit}</span> */}
                  {row.original?.available_stock?.toLocaleString()}{" "}
                  <span className="inventory-summary-stock-unit">{unit}</span>
                </span>
              );
            },
          },
          {
            header: "Status",
            accessorKey: "status",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              return getStatusBadge(row.original);
            },
          },
        ];

      case "pending_orders":
        return [
          // {
          //   header: "Sr No.",
          //   accessorKey: "sr_no",
          //   enableColumnFilter: false,
          //   enableSorting: false,
          // },
          {
            header: "Order No.",
            accessorKey: "order_no",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_orders_list_filter_dropdown}/order_no`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row?.original?.order_no || "-"}
                </span>
              );
            },
          },
          {
            header: "Quotation No.",
            accessorKey: "quotation_number",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_orders_list_filter_dropdown}/quotation_number`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row?.original?.quotation?.quotation_number || "-"}
                </span>
              );
            },
          },
          {
            header: "Order Date",
            accessorKey: "order_date",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "date" as const,
            cell: ({ row }: { row: any }) => {
              const date = row.original.order_date
                ? new Date(row.original.order_date).toISOString().split("T")[0]
                : "N/A";
              return <span className="quick-actions-table-styles">{date}</span>;
            },
          },

          {
            header: "Customer Name",
            accessorKey: "customer_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_orders_list_filter_dropdown}/customer_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row?.original?.customer?.customer_name || "-"}
                </span>
              );
            },
          },
          {
            header: "Dispatch from warehouse",
            accessorKey: "warehouse_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: "/order/dropdown/warehouse_name",
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row?.original?.warehouse?.warehouse_name || "-"}
                </span>
              );
            },
          },
          {
            header: "Order Status",
            accessorKey: "order_status",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            // filterFetchUrl: "/order/dropdown/order_status",
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_orders_list_filter_dropdown}/order_status`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <OrderStatusBadge status={row?.original?.order_status || "-"} />
              );
            },
          },
        ];

      case "quotation_approval":
        return [
          // {
          //   header: "Sr No.",
          //   accessorKey: "sr_no",
          //   enableColumnFilter: false,
          //   enableSorting: false,
          //   cell: ({ row }: { row: any }) => row.original.sr_no || "-",
          // },
          {
            header: "Enquiry No.",
            accessorKey: "enquiry_number",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_quotation_approval_list_filter_dropdown}/enquiry_number`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => (
              <span className="quick-actions-table-styles">
                {row.original.enquiry_number || "-"}
              </span>
            ),
          },
          {
            header: "Enquiry Date",
            accessorKey: "enquiry_date",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "date" as const,
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.enquiry_date?.split("T")?.[0]}
                </span>
              );
            },
          },
          {
            header: "Customer Name",
            accessorKey: "customer_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_quotation_approval_list_filter_dropdown}/customer_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.customer_details?.customer_name || "-"}
                </span>
              );
            },
          },
          {
            header: "Phone Number",
            accessorKey: "contact_person_phone_no",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.customer_details?.contact_person_phone_no ||
                    "-"}
                </span>
              );
            },
          },
          {
            header: "Site Visit",
            accessorKey: "site_visit_required",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              return (
                <span className="quick-actions-table-styles">
                  {row.original.site_visit_required ? "Yes" : "No"}
                </span>
              );
            },
          },
          {
            header: "Staff Name",
            accessorKey: "staff_name",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_quotation_approval_list_filter_dropdown}/staff_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              // Get staff name from site_visit object (denormalized field)
              // staff_details is an object, but staff_name is the denormalized string field
              return (
                <span className="quick-actions-table-styles">
                  {row.original.site_visit?.staff_name || "-"}
                </span>
              );
            },
          },
          {
            header: "Site Visit Date",
            accessorKey: "site_visit_date",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "date" as const,
            // filterFetchUrl: buildWarehouseUrl(
            //   `${api?.dashboad?.quick_action_quotation_approval_list_filter_dropdown}/enquiry_number`,
            //   warehouse
            // ),
            cell: ({ row }: { row: any }) => {
              // return formatDate(row.original.site_visit?.site_visit_date) || "-";
              const raw = row.original?.site_visit?.site_visit_date;
              if (!raw)
                return <span className="quick-actions-table-styles">-</span>;

              const d = new Date(raw);
              return (
                <span className="quick-actions-table-styles">
                  {d.toISOString().split("T")[0]}
                </span>
              );
            },
          },
        ];

      case "pending_dispatch":
        return [
          // {
          //   header: "Sr. No.",
          //   accessorKey: "sr_no",
          //   enableColumnFilter: false,
          //   enableSorting: false,
          //   cell: ({ row }: { row: any }) => {
          //     return (
          //       row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          //     );
          //   },
          // },

          {
            header: "Order No.",
            accessorKey: "order_no",
            enableSorting: true,
            enableColumnFilter: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_dispatch_list_filter_dropdown}/order_no`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => (
              <span className="quick-actions-table-styles">
                {row.original.order_no || "-"}
              </span>
            ),
          },
          {
            header: "Quotation No.",
            accessorKey: "quotation_number",
            enableSorting: true,
            enableColumnFilter: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_dispatch_list_filter_dropdown}/quotation_number`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => (
              <span className="quick-actions-table-styles">
                {row.original?.quotation?.quotation_number || "-"}
              </span>
            ),
          },

          {
            header: "Order Date",
            accessorKey: "order_date",
            enableSorting: true,
            enableColumnFilter: true,
            filterType: "date" as const,
            filterFieldFrom: "order_date_from",
            filterFieldTo: "order_date_to",
            cell: ({ row }: { row: any }) => {
              const date = row.original.order_date
                ? new Date(row.original.order_date).toISOString().split("T")[0]
                : "-";
              return <span className="quick-actions-table-styles">{date}</span>;
            },
          },
          {
            header: "Order Status",
            accessorKey: "order_status",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_dispatch_list_filter_dropdown}/order_status`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => {
              return (
                <OrderStatusBadge status={row?.original?.order_status || "-"} />
              );
            },
          },

          {
            header: "Customer Name",
            accessorKey: "customer_name",
            enableSorting: true,
            enableColumnFilter: true,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_dispatch_list_filter_dropdown}/customer_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => (
              <span className="quick-actions-table-styles">
                {row.original.customer_details?.customer_name || "-"}
              </span>
            ),
          },

          {
            header: "Dispatch From Warehouse",
            accessorKey: "warehouse_name",
            enableSorting: false,
            enableColumnFilter: false,
            filterType: "dropdown" as const,
            filterFetchUrl: buildWarehouseUrl(
              `${api?.dashboad?.quick_action_pending_dispatch_list_filter_dropdown}/warehouse_name`,
              warehouse
            ),
            cell: ({ row }: { row: any }) => (
              <span className="quick-actions-table-styles">
                {row.original.warehouse?.warehouse_name || "-"}
              </span>
            ),
          },
        ];

      default:
        return [];
    }
  }, [selectedActionType, warehouse, buildWarehouseUrl]);

  const selectedAction = quickActionsData.find(
    (a: any) => a.actionType === selectedActionType
  );
  const SelectedIcon = selectedAction?.icon || Package;

  return (
    <>
      <Card className="quick-actions-main-card shadow-sm border-0 w-100 h-100">
        <Card.Body className="p-4">
          {/* Header */}
          <div className="mb-3 pb-2 border-bottom position-relative">
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="quick-actions-header-icon">
                <AlertTriangle size={24} className="text-warning" />
              </div>
              <div>
                <h3
                  className="mb-0 fw-bold text-dark"
                  style={{ fontSize: "1.125rem" }}
                >
                  Quick Actions
                </h3>
              </div>
            </div>
            <div className="position-absolute top-0 end-0 d-flex align-items-center gap-2">
              <AlertTriangle className="text-danger" size={16} />
              <span
                className="text-danger fw-medium"
                style={{ fontSize: "0.875rem" }}
              >
                Requires Attention
              </span>
            </div>
          </div>

          <div className="quick-actions-grid">
            {quickActionsData.map((action: any) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  className="quick-action-card"
                  onClick={() => handleCardClick(action.actionType)}
                  disabled={quickActionsLoading}
                >
                  <div className="quick-action-card-header">
                    <div
                      className={`quick-action-icon-container ${action.iconBgClass}`}
                    >
                      <Icon
                        className={`quick-action-icon ${action.iconColorClass}`}
                      />
                    </div>
                    <span className="quick-action-count">
                      {quickActionsLoading ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "2rem" }}>
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : (
                        action.count
                      )}
                    </span>
                  </div>

                  <h3 className="quick-action-title">{action.title}</h3>
                  <p className="quick-action-description">
                    {action.description}
                  </p>

                  <div className="quick-action-footer">
                    <span className="quick-action-link">
                      {action.actionText}
                    </span>
                    <ChevronRight className="quick-action-chevron" />
                  </div>
                </button>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Slide-out Panel */}
      <DashboardSlidePanel
        isOpen={isPanelOpen && !!selectedActionType}
        onClose={() => setIsPanelOpen(false)}
        icon={SelectedIcon}
        title={getPanelTitle()}
        subtitle={`${totalRecords} items found`}
        loading={listLoading}
        columns={columns}
        data={quickActionList}
        totalRecords={totalRecords}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={filterValue}
        setFilterValue={setFilterValue}
        sorting={sorting}
        onSortingChange={setSorting}
        searchPlaceholder={`Search ${getPanelTitle().toLowerCase()}...`}
      />
    </>
  );
}
